import { useMemo } from "react";

import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { DataTable } from "components/DataTable/DataTable";
import { ZoneBreadcrumbs } from "components/ZoneBreadcrumbs/ZoneBreadcrumbs";
import { useNavigate } from "hooks/useNavigate";
import { useZoneBreadcrumbPath } from "hooks/useZoneBreadcrumbPath";
import { IDevice } from "interfaces/device.interface";
import { IZone } from "interfaces/zone.interface";

import {
  DeviceStatus,
  deviceStatusSort,
} from "../../components/TableRowComponents";

type DeviceTableRow = {
  id: string;
  deviceName: string | null;
  zoneName: string | null;
  subjectName: string | null;
  serialNumber: string | null;
  deviceStatus: boolean | null;
};

type IProps = {
  devices: IDevice[];
  zoneEntity: Record<string, IZone>;
};

export function DeviceList({ devices, zoneEntity }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breadcrumbPath = useZoneBreadcrumbPath();

  const columns = useMemo<ColumnDef<DeviceTableRow, any>[]>(
    () => [
      {
        accessorKey: "deviceName",
        header: t("Device Name / Location"),
      },
      {
        accessorKey: "serialNumber",
        header: t("Serial Number"),
      },
      {
        accessorKey: "zoneName",
        header: t("Zone"),
      },
      {
        accessorKey: "subjectName",
        header: t("Subject ID"),
      },
      {
        accessorKey: "deviceStatus",
        header: t("Status"),
        cell: DeviceStatus,
        sortingFn: deviceStatusSort,
      },
    ],
    [t],
  );

  const data = useMemo(
    (): DeviceTableRow[] =>
      devices.map((d) => ({
        id: d.id,
        deviceName: d.name ?? null,
        serialNumber: d.serial_number ?? null,
        deviceStatus: d.online ?? null,
        zoneName: d.zone_id ? (zoneEntity[d.zone_id]?.name ?? null) : null,
        subjectName: d.subject?.data?.identifier ?? t("none_unassign_device"),
      })),
    [devices, zoneEntity, t],
  );

  return (
    <DataTable
      tableId={"device-list"}
      columns={columns}
      data={data}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(row.id)}
      topContent={<ZoneBreadcrumbs path={breadcrumbPath} />}
      footerText={t("device_count", { count: data.length })}
    />
  );
}
